import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users } from "lucide-react";
import { useAgencyMembers } from "@/hooks/platform/useAgency";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";

const CreatorRosterPanel = () => {
  const { data: creators = [], isLoading, error, refetch } = useAgencyMembers("creator");
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return creators;
    return creators.filter((c) =>
      [c.username, c.full_name, c.business_name, c.portal_id, c.city].some((v) => (v ?? "").toLowerCase().includes(t)),
    );
  }, [creators, search]);

  if (isLoading) return <LoadingState label="Loading creator roster…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">{creators.length} creators under management</p>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search creator or portal ID"
          className="w-64 bg-zinc-950 border-zinc-800 text-white"
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Users} title="No creators yet" description="Creators appear here as soon as they join the platform." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((c) => (
            <Card key={c.id} className="bg-white/5 border-white/20">
              <CardContent className="p-4 flex gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={c.avatar_url ?? undefined} />
                  <AvatarFallback>{(c.full_name || c.username).slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white truncate">{c.full_name || c.username}</p>
                  <p className="text-xs text-gray-400 truncate">@{c.username}</p>
                  <p className="text-[11px] font-mono text-gray-500 mt-1">{c.portal_id ?? "—"}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Badge className="bg-white/10 border-0 text-xs capitalize">
                      {(c.verification_status ?? "unverified").replace("_", " ")}
                    </Badge>
                    {c.city && <Badge className="bg-white/10 border-0 text-xs">{c.city}</Badge>}
                    <Badge className="bg-white/10 border-0 text-xs">{c.profile_views ?? 0} views</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreatorRosterPanel;
